import React, { useState } from "react";
import { Label, Input, Switch, Textarea } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Sparkles, HelpCircle, FlaskConical, Tag } from "lucide-react";

interface FormRequestSettingsViewProps {
  isTest: boolean;
  onIsTestChange: (checked: boolean) => void;
  useAiPersonalization: boolean;
  onUseAiPersonalizationChange: (checked: boolean) => void;
  contextInstructions: string;
  onContextInstructionsChange: (val: string) => void;
  internalIdentifier: string;
  onInternalIdentifierChange: (val: string) => void;
  contextPlaceholder?: string;
  onHelpClick: () => void;
  hideAiPersonalization?: boolean;
  playerLabel?: string;
  fanLabel?: string;
}

export function FormRequestSettingsView({
  isTest,
  onIsTestChange,
  useAiPersonalization,
  onUseAiPersonalizationChange,
  contextInstructions,
  onContextInstructionsChange,
  internalIdentifier,
  onInternalIdentifierChange,
  contextPlaceholder = "e.g. This fan closed on a VA loan. Focus on their VA benefits, home buying experience, and military background...",
  onHelpClick,
  hideAiPersonalization = false,
  playerLabel,
  fanLabel,
}: FormRequestSettingsViewProps) {
  // Automatically pre-expand context if instructions already exist (e.g. in Edit mode or back-navigation)
  const [showContextInput, setShowContextInput] = useState(!!contextInstructions);

  return (
    <div className="space-y-5 py-2 animate-in fade-in duration-200 select-none">
      {/* Header Description */}
      <div className="flex flex-col gap-1 mb-1">
        <h3 className="text-base font-bold text-slate-800 font-sans leading-none">Configure Request Settings</h3>
        <p className="text-xs text-slate-500 leading-normal font-sans">
          Fine-tune the behavior, tracking parameters, and AI-driven customization for this request link.
        </p>
      </div>

      {/* Compact Grid: CRM Tracking & Test Run (Side-by-Side to save vertical space!) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CRM Tracking Identifier */}
        <div className="flex flex-col gap-2 p-4 rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center gap-2">
            <Tag className="size-4 text-slate-400 shrink-0" />
            <Label className="font-bold text-xs text-slate-500 uppercase tracking-wide">Internal Identifier</Label>
          </div>
          <Input
            value={internalIdentifier}
            onChange={e => onInternalIdentifierChange(e.target.value)}
            placeholder="e.g. LOAN-100234 or DEAL-XYZ"
            className="bg-slate-50 border-slate-200 text-xs h-9 focus:bg-white transition-colors"
          />
          <p className="text-xs text-muted-foreground leading-normal font-sans mt-0.5">
            Reference string used to map this request back to your external CRM systems.
          </p>
        </div>

        {/* Test Request Panel (Takes up less space!) */}
        <div
          onClick={() => onIsTestChange(!isTest)}
          className={cn(
            "flex flex-col justify-between gap-3 p-4 rounded-xl border transition-all duration-150 cursor-pointer select-none",
            isTest
              ? "border-amber-400 bg-amber-50/10 hover:bg-amber-50/15"
              : "border-slate-200 bg-white hover:bg-slate-50/50 hover:border-slate-300"
          )}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <FlaskConical className={cn("size-4 shrink-0", isTest ? "text-amber-500" : "text-slate-400")} />
              <Label className="font-bold text-xs text-slate-500 uppercase tracking-wide cursor-pointer">
                Test Request Run
              </Label>
            </div>
            <Switch checked={isTest} onCheckedChange={onIsTestChange} onClick={e => e.stopPropagation()} />
          </div>
          <p className="text-xs text-muted-foreground leading-normal font-sans">
            Configure as a test run to prevent dirtying production analytics and review dashboards.
          </p>
        </div>
      </div>

      {/* AI Personalization Suite (Dynamic, beautiful Lavender Card) */}
      {!hideAiPersonalization && (
        <div
          className={cn(
            "rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col",
            useAiPersonalization
              ? "border-purple-300 bg-purple-50/5 shadow-2xs"
              : "border-slate-200 bg-white hover:border-slate-300"
          )}
        >
          {/* Top Panel: Toggle Card */}
          <div
            onClick={() => {
              const nextState = !useAiPersonalization;
              onUseAiPersonalizationChange(nextState);
              // Auto-expand/collapse input state matching the switch state
              if (!nextState) setShowContextInput(false);
            }}
            className="flex items-center justify-between gap-4 p-5 cursor-pointer select-none hover:bg-slate-100/10 transition duration-150"
          >
            <div className="flex flex-col gap-1 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  className={cn(
                    "size-8 rounded-lg flex items-center justify-center transition duration-200",
                    useAiPersonalization ? "bg-purple-600 text-white animate-pulse" : "bg-slate-100 text-slate-500"
                  )}
                >
                  <Sparkles className="size-4" />
                </div>
                <Label className="font-bold text-sm text-slate-800 cursor-pointer">AI Personalization</Label>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onHelpClick();
                  }}
                  className="text-xs text-primary hover:underline font-bold focus:outline-none flex items-center gap-1 bg-primary/10 px-2 py-0.5 rounded leading-none"
                >
                  <span>See How It Works</span>
                  <HelpCircle className="size-3 shrink-0" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground leading-normal font-sans mt-1.5 max-w-lg">
                Enable custom guidelines and context for our AI engine to automatically personalize form titles and
                instructions.
              </p>
            </div>
            <Switch
              checked={useAiPersonalization}
              onCheckedChange={checked => {
                onUseAiPersonalizationChange(checked);
                if (!checked) setShowContextInput(false);
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Bottom Panel: Nested Context Instructions (Toggled Optional Dropdown!) */}
          {useAiPersonalization && (
            <>
              {!showContextInput ? (
                /* Collapsed State: Display reassurance summary on the left (if available) and trigger button on the right */
                <div className="border-t border-purple-100 bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-in fade-in duration-200 select-none">
                  {playerLabel || fanLabel ? (
                    <div className="flex-1 min-w-0 flex flex-col gap-1 text-left">
                      <span className="text-xs font-bold text-slate-700 font-sans block leading-none">
                        Automatic AI Personalization Active
                      </span>
                      <p className="text-[11px] md:text-xs text-slate-500 leading-relaxed font-sans max-w-sm mt-0.5">
                        Our engine will dynamically customize form titles and instructions using profile details from:
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {playerLabel && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 leading-none">
                            <span className="size-1.5 rounded-full bg-indigo-500 shrink-0" />
                            Player: {playerLabel}
                          </span>
                        )}
                        {fanLabel && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-100 leading-none">
                            <span className="size-1.5 rounded-full bg-purple-500 shrink-0" />
                            Client: {fanLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className={cn("shrink-0", !(playerLabel || fanLabel) && "w-full flex justify-center")}>
                    <button
                      type="button"
                      onClick={() => setShowContextInput(true)}
                      className="text-xs text-purple-700 hover:text-purple-800 hover:bg-purple-50/80 px-4 py-2 rounded-xl border border-dashed border-purple-200 hover:border-purple-300 font-bold flex items-center gap-2 transition-all duration-200 focus:outline-none cursor-pointer"
                    >
                      <span>+ Add Custom Guidelines & Context (Optional)</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* Expanded Textarea State */
                <div className="border-t border-purple-100 bg-white p-5 space-y-3 animate-in slide-in-from-top-3 duration-300 flex flex-col">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-purple-600 uppercase tracking-wide">
                      Custom Guidelines & Context (Optional)
                    </Label>
                    <button
                      type="button"
                      onClick={() => setShowContextInput(false)}
                      className="text-xs text-slate-400 hover:text-slate-600 hover:underline font-semibold focus:outline-none cursor-pointer"
                    >
                      Hide Guidelines
                    </button>
                  </div>
                  <Textarea
                    value={contextInstructions}
                    onChange={e => onContextInstructionsChange(e.target.value)}
                    placeholder={contextPlaceholder}
                    className="bg-slate-50 border-slate-200 text-xs min-h-[110px] focus:bg-white focus:border-purple-400 transition-colors"
                  />
                  <p className="text-xs text-muted-foreground leading-normal font-sans">
                    Type unique customer or loan variables here. The AI will utilize this specific context to craft
                    custom headings and success responses.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
